import {
  useDisableTwoFactor,
  useEnableTwoFactor,
  useSetSecurityMethod,
  useSetupTwoFactor,
} from '@/hooks/useAuth';
import { TwoFactorVerifyData } from '@/types/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Shield,
  Smartphone,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

const verificationSchema = z.object({
  token: z
    .string()
    .length(6, 'auth.validation.twoFactor.tokenLength')
    .regex(/^\d+$/, 'auth.validation.twoFactor.tokenDigits'),
});

const disableSchema = z.object({
  password: z.string().min(1, 'auth.validation.password.required'),
});

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  enabled: boolean;
  onSuccess?: () => void;
}

export const TwoFactorSetupModal = ({
  isOpen,
  onClose,
  enabled,
  onSuccess,
}: TwoFactorSetupModalProps) => {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [showDisableForm, setShowDisableForm] = useState(false);

  const {
    mutate: setupMutate,
    isPending: isSetupPending,
    isError: isSetupError,
  } = useSetupTwoFactor();
  const enableTwoFactor = useEnableTwoFactor();
  const disableTwoFactor = useDisableTwoFactor();
  const setSecurityMethod = useSetSecurityMethod();

  const {
    register: registerVerify,
    handleSubmit: handleVerifySubmit,
    formState: { errors: verifyErrors, isSubmitting: isVerifying },
    reset: resetVerify,
  } = useForm<{ token: string }>({
    resolver: zodResolver(verificationSchema),
  });

  const {
    register: registerDisable,
    handleSubmit: handleDisableSubmit,
    formState: { errors: disableErrors, isSubmitting: isDisabling },
    reset: resetDisable,
  } = useForm<{ password: string }>({
    resolver: zodResolver(disableSchema),
  });

  // Setup 2FA when modal opens (for enabling)
  useEffect(() => {
    if (isOpen && !enabled) {
      setupMutate(undefined, {
        onSuccess: (response) => {
          setQrCode(response.data.qrCode);
          setSecret(response.data.secret);
        },
      });
    }
  }, [isOpen, enabled, setupMutate]);

  const handleRetrySetup = () => {
    setupMutate(undefined, {
      onSuccess: (response) => {
        setQrCode(response.data.qrCode);
        setSecret(response.data.secret);
      },
    });
  };

  const handleCopySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      toast.success(t('settings.twoFactor.secretCopied'));
    }
  };

  const onVerifySubmit = async (data: { token: string }) => {
    try {
      const verifyData: TwoFactorVerifyData = {
        token: data.token,
      };
      await enableTwoFactor.mutateAsync(verifyData);
      await setSecurityMethod.mutateAsync('totp');
      resetVerify();
      setQrCode(null);
      setSecret(null);
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  const onDisableSubmit = async (data: { password: string }) => {
    try {
      await disableTwoFactor.mutateAsync({ password: data.password });
      await setSecurityMethod.mutateAsync(null);
      resetDisable();
      setShowDisableForm(false);
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  const handleClose = () => {
    resetVerify();
    resetDisable();
    setQrCode(null);
    setSecret(null);
    setShowDisableForm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`relative w-full rounded-2xl bg-white p-6 shadow-xl transition-all ${!enabled && qrCode ? 'max-w-4xl' : 'max-w-md'
          }`}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Enable 2FA Flow */}
        {!enabled && (
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <Shield className="h-8 w-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {t('settings.twoFactor.setupTitle')}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {t('settings.twoFactor.setupDescription')}
              </p>
            </div>

            {/* QR Code Section */}
            {qrCode && (
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        1
                      </div>
                      <p className="font-medium text-neutral-900">
                        {t('settings.twoFactor.step1')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <Smartphone className="h-4 w-4" />
                      <span>{t('settings.twoFactor.downloadApp')}</span>
                    </div>
                    <p className="mt-2 text-xs text-neutral-500">
                      Google Authenticator, Authy, Microsoft Authenticator
                    </p>
                  </div>

                  {/* Step 2 - QR Code */}
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        2
                      </div>
                      <p className="font-medium text-neutral-900">
                        {t('settings.twoFactor.step2')}
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <img
                        src={qrCode}
                        alt="QR Code"
                        className="h-48 w-48 rounded-lg border border-neutral-300 bg-white p-2"
                      />
                    </div>
                    <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3">
                      <p className="mb-1 text-xs font-medium text-neutral-700">
                        {t('settings.twoFactor.manualEntry')}
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="flex-1 break-all font-mono text-sm text-neutral-900">
                          {secret}
                        </code>
                        <button
                          onClick={handleCopySecret}
                          className="rounded-md p-1.5 text-neutral-600 transition-colors hover:bg-neutral-100"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3 - Verification */}
                <div className="flex flex-col justify-center">
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        3
                      </div>
                      <p className="font-medium text-neutral-900">
                        {t('settings.twoFactor.step3')}
                      </p>
                    </div>

                    <form onSubmit={handleVerifySubmit(onVerifySubmit)}>
                      <div className="mb-6">
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <KeyRound className="h-5 w-5 text-neutral-400" />
                          </div>
                          <input
                            {...registerVerify('token')}
                            type="text"
                            maxLength={6}
                            placeholder="000000"
                            className="block w-full rounded-lg border border-neutral-300 py-3 pl-10 pr-3 text-center text-2xl font-mono tracking-[0.5em] shadow-sm transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            pattern="\d{6}"
                          />
                        </div>
                        {verifyErrors.token && (
                          <p className="mt-1 text-sm text-error-600">
                            {t(verifyErrors.token.message as string)}
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isVerifying}
                        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 text-base font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                      >
                        <Check className="h-5 w-5" />
                        {isVerifying
                          ? t('common.verifying')
                          : t('settings.twoFactor.enable')}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Loading */}
            {!qrCode && isSetupPending && (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
              </div>
            )}

            {/* Error State */}
            {!qrCode && isSetupError && (
              <div className="rounded-lg border border-red-200 bg-error-50 p-6 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-error-100">
                  <AlertTriangle className="h-6 w-6 text-error-600" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-error-900">
                  {t('auth.messages.twoFactor.setupError')}
                </h3>
                <div className="mt-6 flex justify-center gap-3">
                  <button
                    onClick={handleClose}
                    className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleRetrySetup}
                    className="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-error-700"
                  >
                    {t('common.retry', 'Réessayer')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Disable 2FA Flow */}
        {enabled && (
          <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-warning-100">
                <AlertTriangle className="h-8 w-8 text-warning-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {t('settings.twoFactor.disableTitle')}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {t('settings.twoFactor.disableWarning')}
              </p>
            </div>

            {!showDisableForm ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-warning-200 bg-warning-50 p-4">
                  <p className="text-sm text-warning-800">
                    {t('settings.twoFactor.disableConfirm')}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => setShowDisableForm(true)}
                    className="flex-1 rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-error-700"
                  >
                    {t('settings.twoFactor.continueDisable')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleDisableSubmit(onDisableSubmit)}>
                <div className="mb-4">
                  <label
                    htmlFor="password"
                    className="mb-2 block text-sm font-medium text-neutral-700"
                  >
                    {t('settings.twoFactor.confirmPassword')}
                  </label>
                  <input
                    {...registerDisable('password')}
                    type="password"
                    id="password"
                    className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                    placeholder={t('auth.login.passwordPlaceholder')}
                  />
                  {disableErrors.password && (
                    <p className="mt-1 text-sm text-error-600">
                      {t(disableErrors.password.message as string)}
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isDisabling}
                    className="flex-1 rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-error-700 disabled:opacity-50"
                  >
                    {isDisabling
                      ? t('common.disabling')
                      : t('settings.twoFactor.disable')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
